/**
 * Provider-error card helpers.
 *
 * The Gemini PROVIDER_ERROR path renders an inline banner with retry/fallback
 * actions. Anthropic auth/credit failures (#1057) instead render an actionable
 * guidance card telling the user to run `claude login`. This module isolates the
 * pure decision + copy so it can be unit-tested by setpoint-evals without a DOM.
 */

export interface ProviderErrorState {
  provider: string;
  message: string;
  offerFallback: boolean;
  authRequired?: boolean;
  remedy?: 'claude_login';
}

/**
 * True when the provider error is an Anthropic credential/credit failure that
 * should render the "run claude login" guidance card (not the inline banner).
 * The Gemini (`google`) path is intentionally excluded — it keeps its banner.
 */
export function isAnthropicAuthCard(
  err: ProviderErrorState | null | undefined,
): boolean {
  return !!err && err.provider === 'anthropic' && err.authRequired === true;
}

/** User-facing copy for the Anthropic auth guidance card. */
export const ANTHROPIC_AUTH_CARD_COPY = {
  title: 'Claude needs to sign in',
  body:
    'The agent could not authenticate with Anthropic. Your dev box needs valid Claude credentials. ' +
    'Run `claude login` in your dev-box shell, or open the SSH terminal here and run it — then send your message again.',
  steps: [
    'Open a terminal on your dev box (or the in-app SSH terminal).',
    'Run `claude login` and complete the sign-in.',
    'Send your message again.',
  ],
} as const;
